import { useSearchParams } from "#routers/hooks";
import { EquipmentList } from "#routes/organization/equipment/EquipmentList";
import { ApplyAutomationProgressBanner } from "#src/batteries-included-components/Banners/ApplyAutomationProgressBanner";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus";
import { CreateAssetDropdownMenu } from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu";
import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import {
  EquipmentRecordsTableFilterArea,
  EquipmentRecordsViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/RecordsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RecordsListTablePanel } from "#src/batteries-included-components/Panels/TablePanels/RecordsListTablePanel";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { EquipmentListRoute } from "#src/routes/organization/equipment";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  generateDocumentTitle,
  Page,
  Tab,
} from "@validereinc/common-components";
import { AssetType, ResourceDefinitions, Resources } from "@validereinc/domain";
import React from "react";
import { Titled } from "react-titled";

const DEFAULT_TAB_KEY = "list";

export const EquipmentListPage = () => {
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [calcsAndRecordsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "calculations-records-shared"
  );
  const [
    filterConfigStorageKey,
    tableConfigurationStorageKey,
    tableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    ["records", "list", "equipment"]
  );
  const [recordsApplyAutomationJobStorageKey] = useStorageKey(
    ["async-job"],
    ["records", "list", "equipment", "apply-automation"]
  );
  const [configsApplyAutomationJobStorageKey] = useStorageKey(
    ["async-job"],
    "equipment-configs-apply-automation"
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { localize, isLoading } = useLocalization();
  const [breadcrumbs] = useBreadcrumbsFromRoute(EquipmentListRoute, {
    "/equipment": {
      title: localize(`${Resources.EQUIPMENT}_plural`),
    },
  });

  const actionRow = [
    <IsFeatureAvailable
      key="create-equipment"
      featureFlagQuery="core:equipment"
      permissionQuery="equipment:write"
      fallbackChildren={null}
    >
      <CreateAssetDropdownMenu assetType={AssetType.EQUIPMENT} />
    </IsFeatureAvailable>,
  ];

  const importDataActionQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.record,
      label: {
        singular: `${localize(ResourceDefinitions.equipment.label.singular)} ${ResourceDefinitions.record.label.singular}`,
        plural: `${localize(ResourceDefinitions.equipment.label.singular)} ${ResourceDefinitions.record.label.plural}`,
      },
    },
  });

  const pageTitle = localize("equipment_plural");

  return (
    <Page
      category={EquipmentListRoute.subtitle}
      title={pageTitle}
      breadcrumbs={breadcrumbs}
      isLoading={isLoading}
      actionRow={actionRow}
      activeTabKey={searchParams?.tab ?? DEFAULT_TAB_KEY}
      onActiveTabKeyChange={(tabKey: string) => {
        setSearchParams({ tab: tabKey });
      }}
    >
      <Tab
        title="List"
        tabKey="list"
      >
        <EquipmentList />
      </Tab>
      <Tab
        title="Records"
        tabKey="records"
      >
        <Titled
          title={generateDocumentTitle(
            `${breadcrumbs.at(-2)?.title ?? localize(Resources.EQUIPMENT)} ${ResourceDefinitions.record.label.plural}`
          )}
        >
          {isDataIngestionEnabled ? (
            <ImportDataActionStatusWithQueue
              queueKey={importDataActionQueueKey}
            />
          ) : null}
          <ApplyAutomationProgressBanner
            jobStorageKey={recordsApplyAutomationJobStorageKey}
            showCounts={true}
          />
          <ApplyAutomationProgressBanner
            jobStorageKey={configsApplyAutomationJobStorageKey}
            showCounts={false}
          />
          <EquipmentRecordsViewFilterArea
            viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
          />
          <RecordsListTablePanel
            type={AssetType.EQUIPMENT}
            filterConfigStorageKey={filterConfigStorageKey}
            tableConfigStorageKey={tableConfigurationStorageKey}
            tableStateStorageKey={tableStateStorageKey}
            viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
            applyAutomationJobStorageKey={recordsApplyAutomationJobStorageKey}
            applySeededAutomationJobStorageKey={
              configsApplyAutomationJobStorageKey
            }
            filterComponentSlot={({ recordValueTypes }) => (
              <EquipmentRecordsTableFilterArea
                filterConfigStorageKey={filterConfigStorageKey}
                selectableProperties={recordValueTypes}
              />
            )}
          />
        </Titled>
      </Tab>
    </Page>
  );
};
